import React, { FC, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { GkvEditSummaryProps } from "../types/interface";
import { colors } from "../../GKVNew/styles/tokens";

/** A single stat: number on top, label below. Optional highlight color on the number */
const Stat: FC<{ label: string; value: string | number; highlight?: string }> = ({
  label,
  value,
  highlight,
}) => (
  <Box sx={{ textAlign: "center", px: 1 }}>
    <Typography
      sx={{
        fontSize: "1.3rem",
        fontWeight: 700,
        lineHeight: 1,
        color: highlight || colors.textOnDark,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.75rem",
        color: colors.textOnDark,
        lineHeight: 1,
        mt: 0.3,
      }}
    >
      {label}
    </Typography>
  </Box>
);

/** Subtle vertical separator between stats */
const Divider = () => (
  <Box sx={{ width: "1px", height: 20, backgroundColor: "rgba(255,255,255,0.2)" }} />
);

/**
 * Summary bar showing key metrics — matches GKVNew GkvBillSummary design.
 */
const GkvEditSummary: FC<GkvEditSummaryProps> = ({ bills, validationMap }) => {
  const stats = useMemo(() => {
    const allRxs = bills.flatMap((b) => b.rxs ?? []);
    const totalRxs = allRxs.length;
    const totalAmount = bills.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
    const taxiertRxs = allRxs.filter((rx) => rx.isTaxieren).length;

    // Validation counts
    let validRxs = 0;
    for (const rx of allRxs) {
      const validators = validationMap[rx.id];
      if (!validators) continue;
      const values = Object.values(validators);
      if (!values.some((v) => v.validity === "Not Valid")) {
        validRxs++;
      }
    }

    return { totalRxs, totalAmount, taxiertRxs, validRxs };
  }, [bills, validationMap]);

  if (bills.length === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        py: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.8rem",
          fontWeight: 600,
          color: colors.textOnDark,
          letterSpacing: "0.3px",
          whiteSpace: "nowrap",
        }}
      >
        Zusammenfassung
      </Typography>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          ml: 2,
        }}
      >
        <Stat label="Rechnungen" value={bills.length} />
        <Divider />
        <Stat label="Rezepte" value={stats.totalRxs} />
        <Divider />
        <Stat
          label="Taxiert"
          value={stats.taxiertRxs}
          highlight={stats.taxiertRxs === stats.totalRxs ? colors.valid : colors.lightWarning}
        />
        <Divider />
        <Stat
          label="Valide"
          value={stats.validRxs}
          highlight={stats.validRxs === stats.totalRxs ? colors.valid : undefined}
        />
        <Divider />
        <Stat
          label="Gesamt"
          value={`${Number(stats.totalAmount).toFixed(2).replace(".", ",")} €`}
          highlight={colors.infoBg}
        />
      </Box>
    </Box>
  );
};

export default React.memo(GkvEditSummary);
